"use client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { IconPlus, IconTestPipe } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import blockTypes, { IBlock } from "@/lib/blockTypes";
import { ReactSortable } from "react-sortablejs";
import { v4 as uuidv4 } from "uuid";
import { Card } from "@/components/ui/card";
import React from "react";
import { IUserData } from "@/types";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface Props {
    random?: number;
    brand?: boolean;
    account: IUserData;
    inBlocks?: boolean;
}
interface PropsBlock {
    random?: number;
    brand?: boolean;
    account: IUserData;
    isMobile: boolean;
}
export default function AddBlock({ brand, account, inBlocks }: Props) {
    const isMobile = useIsMobile();
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != "-1") {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: "-1",
                },
            })
        );
        if (!isExpanded) {
            const event = new CustomEvent("expandBlock", {
                detail: {
                    content: (
                        <AddBlockContent
                            random={Math.random()}
                            brand={brand}
                            account={account}
                            isMobile={isMobile}
                        />
                    ),
                    title: "Add Block",
                    description:
                        "Drag and drop blocks to add them to your profile",
                },
            });
            window.dispatchEvent(event);
            setIsExpanded(true);
        } else {
            const event = new CustomEvent("collapseBlock");
            window.dispatchEvent(event);
            setIsExpanded(false);
        }
    };
    useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != "0") {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });

    return (
        <Button
            // size="icon"
            className={cn(
                ` group relative flex h-auto shrink-0 gap-2 rounded-md bg-muted text-sm text-foreground transition-all duration-300 hover:bg-primary hover:text-primary-foreground ${isExpanded ? "bg-primary text-primary-foreground" : "bg-muted"}`,
                inBlocks &&
                    "baseFont onboarding-add-block w-fit bg-primary font-bold duration-150 ease-in-out hover:scale-110",
                inBlocks && isExpanded && "bg-primary text-primary-foreground"
            )}
            onClick={handleExpand}
        >
            <IconPlus size={16} />
            <span className="hidden md:block">Add Block</span>
        </Button>
    );
}
const categorizeBlocks = (blocks: any[]) => {
    const categories: any = {};

    blocks.forEach((block) => {
        const category = block?.category;
        if (category) {
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push({
                ...block,
                id: uuidv4(), // Generate a unique ID for each block
            });
        }
    });

    return categories;
};

export const AddBlockContent = ({
    random,
    brand,
    account,
    isMobile,
}: PropsBlock) => {
    // const [_random, _setRandom] = useState(random);
    const blocks = React.useMemo(
        () => blockTypes.filter((e) => !e.v2Render),
        []
    );
    const categories = categorizeBlocks(blocks);
    const [sortableCategories, setSortableCategories] = useState<
        {
            id: any;
            name: string;
            blocks: IBlock[];
        }[]
    >(
        Object.keys(categories).map((category) => ({
            id: uuidv4(),
            name: category,
            blocks: categories[category]?.map((block: any) => ({
                ...block,
                id: uuidv4(),
            })),
        }))
    );
    React.useEffect(() => {
        setSortableCategories(
            Object.keys(categorizeBlocks(blocks)).map((category) => ({
                id: uuidv4(),
                name: category,
                blocks: categorizeBlocks(blocks)[category]?.map(
                    (block: any) => ({
                        ...block,
                        id: uuidv4(),
                    })
                ),
            }))
        );
    }, [blocks, random]);
    return sortableCategories.map((category, index) => {
        category.blocks?.map((block: any) => ({
            ...block,
            id: uuidv4(),
        }));
        return (
            <div key={index} className="mb-2 border-b-2 pb-6 last:border-none">
                <Label variant="toggle" className="py-1 text-lg">
                    {category.name}
                </Label>
                <ReactSortable
                    list={category.blocks}
                    setList={(e) => {
                        // setSortableCategories((prev) => {
                        //     const _category = prev.find(
                        //         (_category) => _category.name === category.name
                        //     );
                        //     if (_category) {
                        //         _category.blocks = e;
                        //     }
                        //     return [...prev];
                        // });
                    }}
                    disabled={isMobile}
                    animation={500}
                    className="flex w-full flex-col gap-2"
                    group={{
                        name: "user-blocks",
                        pull: "clone",
                        put: false,
                    }}
                    clone={(item) =>
                        (
                            // _setRandom(Math.random()),
                            window.dispatchEvent(
                                new CustomEvent("block_added", {
                                    detail: {
                                        id: item.id,
                                    },
                                })
                            ),
                            {
                                ...item,
                            }
                        )
                    }
                    filter={".non-draggable"}
                >
                    {category.blocks?.map((block, index) => {
                        if (
                            block.limited &&
                            !block.limited.some((item) =>
                                account.account_type.includes(item)
                            )
                        ) {
                            return (
                                <Dialog key={block.id}>
                                    <DialogTrigger>
                                        <Card
                                            className={cn(
                                                "relative my-0 w-full overflow-hidden border border-primary bg-popover p-1",
                                                "cursor-pointer",
                                                "non-draggable"
                                            )}
                                            key={block.id}
                                        >
                                            <div
                                                className={cn(
                                                    "flex w-full items-start gap-2",
                                                    "cursor-pointer"
                                                )}
                                            >
                                                <div className="absolute right-0 top-0 overflow-hidden rounded-bl-md bg-primary p-1 font-bold">
                                                    New
                                                </div>
                                                <div className="">
                                                    {block.icon_react}
                                                </div>
                                                <div className="flex w-full flex-col items-start gap-0">
                                                    <Label
                                                        variant="headingtoggle"
                                                        className={cn(
                                                            "cursor-pointer"
                                                        )}
                                                    >
                                                        {block.name}
                                                    </Label>
                                                    <Label
                                                        variant="subtoggle"
                                                        className={cn(
                                                            "line-clamp-2 max-w-sm pr-4 text-left",
                                                            "cursor-pointer"
                                                        )}
                                                    >
                                                        {block.description}
                                                    </Label>
                                                </div>
                                            </div>
                                        </Card>
                                    </DialogTrigger>
                                    <DialogContent className="sm:h-fit">
                                        {/* <DialogHeader>
                                            <DialogTitle>
                                                Early Access
                                            </DialogTitle>
                                            <DialogDescription>
                                                The Fourthwall block is in early
                                                access mode and is currently
                                                only available to paid accounts.
                                            </DialogDescription>
                                        </DialogHeader> */}
                                        <Alert className="text-copy mt-3 w-fit border-primary bg-primary/30">
                                            <div className="flex items-center gap-2">
                                                <IconTestPipe />
                                                <AlertTitle className="mb-0">
                                                    Early Access Feature
                                                </AlertTitle>
                                            </div>
                                            <AlertDescription className="max-w-prose pt-3">
                                                The Fourthwall block is in early
                                                access mode and is currently
                                                only available to paid accounts.
                                            </AlertDescription>
                                        </Alert>
                                        <a href="/admin/settings/billing">
                                            <Button
                                                variant={"cc"}
                                                className="w-full"
                                            >
                                                Learn more
                                            </Button>
                                        </a>
                                    </DialogContent>
                                </Dialog>
                            );
                        }
                        return (
                            <Popover key={block.id}>
                                <PopoverTrigger>
                                    <Card
                                        onClick={() => {
                                            window.dispatchEvent(
                                                new CustomEvent(
                                                    "mobile_block_add",
                                                    {
                                                        detail: {
                                                            block: {
                                                                ...block,
                                                                id: uuidv4(),
                                                            },
                                                        },
                                                    }
                                                )
                                            );
                                        }}
                                        className={cn(
                                            "relative my-0 w-full overflow-hidden bg-popover p-1",
                                            isMobile
                                                ? "cursor-pointer"
                                                : "cursor-grab",
                                            block.type ===
                                                "fourthwall_products" &&
                                                "border border-primary",
                                            block.type === "form" &&
                                                "border border-primary",
                                            block.type === "throne_wishlist" &&
                                                "border border-primary"
                                        )}
                                    >
                                        <div
                                            className={cn(
                                                "flex w-full items-start gap-2",
                                                isMobile
                                                    ? "cursor-pointer"
                                                    : "cursor-grab"
                                            )}
                                        >
                                            {block.type ===
                                                "fourthwall_products" && (
                                                <div className="absolute right-0 top-0 overflow-hidden rounded-bl-md bg-primary p-1 font-bold">
                                                    New
                                                </div>
                                            )}
                                            {block.type === "form" && (
                                                <div className="absolute right-0 top-0 overflow-hidden rounded-bl-md bg-primary p-1 font-bold">
                                                    New
                                                </div>
                                            )}
                                            {block.type ===
                                                "throne_wishlist" && (
                                                <div className="absolute right-0 top-0 overflow-hidden rounded-bl-md bg-primary p-1 font-bold">
                                                    New
                                                </div>
                                            )}
                                            <div className="">
                                                {block.icon_react}
                                            </div>
                                            <div className="flex w-full flex-col items-start gap-0">
                                                <Label
                                                    variant="headingtoggle"
                                                    className={cn(
                                                        isMobile
                                                            ? "cursor-pointer"
                                                            : "cursor-grab"
                                                    )}
                                                >
                                                    {block.name}
                                                </Label>
                                                <Label
                                                    variant="subtoggle"
                                                    className={cn(
                                                        "line-clamp-2 max-w-sm pr-4 text-left",
                                                        isMobile
                                                            ? "cursor-pointer"
                                                            : "cursor-grab"
                                                    )}
                                                >
                                                    {block.description}
                                                </Label>
                                            </div>
                                        </div>
                                    </Card>
                                </PopoverTrigger>
                                <PopoverContent className="p-1 text-sm">
                                    Drag into your profile to add this block
                                </PopoverContent>
                            </Popover>
                        );
                    })}
                </ReactSortable>
            </div>
        );
    });
};
